import { TrashIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardActions,
  CardAuthor,
  CardContent,
} from "@/components/molecules/card/Card";
import { CardGroup } from "@/components/molecules/dragndrop/CardGroup";
import { UserAvatar } from "@/components/molecules/user_avatar/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Textarea } from "@/components/ui/textarea";
import { useRetro } from "@/context/retro/RetroContext.hook";
import { useUser } from "@/context/user/UserContext.hook";
import { type Group, groupCards } from "@/lib/groupCards";
import { pluralText } from "@/lib/pluralText";
import { cn } from "@/lib/utils";

export const DiscussView = () => {
  const { cards, votes, discussionCardId } = useRetro();

  const groups = useMemo(() => {
    return groupCards(cards, votes);
  }, [cards, votes]);

  const discussedGroup = useMemo(() => {
    return groups.find((g) => g.parentCardId === discussionCardId);
  }, [discussionCardId, groups]);

  return (
    <SidebarProvider className={"h-full"}>
      <InAMomentSection groups={groups} />

      {discussedGroup && (
        <CurrentlyDiscussedGroupSection group={discussedGroup} />
      )}

      <ActionPointsSection />
    </SidebarProvider>
  );
};

const InAMomentSection: React.FC<{ groups: Group[] }> = ({ groups }) => {
  const { teamUsers, discussionCardId } = useRetro();

  return (
    <Sidebar
      variant="floating"
      collapsible={"offcanvas"}
      className={"mt-[70px] h-[calc(100%-70px-100px)]"}
    >
      <SidebarHeader>
        <span className={"text-xl overflow-hidden line-clamp-1"}>
          Już za chwilę...
        </span>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup className={"flex flex-col gap-2"}>
          {groups
            .sort((a, b) => b.votes.length - a.votes.length)
            .filter((_, groupIndex) => {
              const discussIndex = groups.findIndex(
                (g) => g.parentCardId === discussionCardId,
              );
              return discussIndex < groupIndex;
            })
            .map((group) => {
              return (
                <CardGroup
                  className={cn(group.votes.length === 0 && "opacity-40")}
                  columnId={"next"}
                  key={`${group.parentCardId}-in-a-moment`}
                  parentCardId={group.parentCardId}
                >
                  {group.cards.map((card, index) => {
                    const author = teamUsers.find(
                      (user) => user.id === card.authorId,
                    );

                    return (
                      <Card
                        id={card.id}
                        key={`${card.id}-in-a-moment`}
                        style={{ marginTop: index === 0 ? 0 : -80 }}
                      >
                        <CardContent text={card.text} />
                        <CardAuthor
                          author={{
                            avatar: author?.avatar_link || "",
                            name: author?.nick || "",
                            id: card.authorId,
                          }}
                        />
                        {group.cards.length === index + 1 && (
                          <CardActions>
                            <div className={"flex justify-center grow w-8"}>
                              <span className={"self-center"}>
                                {group.votes.length}
                              </span>
                            </div>
                          </CardActions>
                        )}
                      </Card>
                    );
                  })}
                </CardGroup>
              );
            })}
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

const CurrentlyDiscussedGroupSection: React.FC<{ group: Group }> = ({
  group,
}) => {
  const { teamUsers } = useRetro();
  const { user } = useUser();

  const hasCurrentUserVotedOnDiscussedGroup = useMemo(() => {
    const votesOnGroup = group.votes;

    return votesOnGroup.some((vote) => vote.voterId === user?.id);
  }, [group, user?.id]);

  return (
    <div className={"pt-4 mx-4 gap-4 flex flex-col grow scrollbar pb-25"}>
      <div className={"flex flex-row gap-2"}>
        <SidebarTrigger variant={"default"} />
        <span>Aktualnie omawiany temat:</span>
      </div>

      <div
        className={cn(
          "flex flex-col bg-card p-2.5 border rounded-2xl wrap-break-word whitespace-pre-line",
          group.votes.length === 0 && "opacity-40",
        )}
      >
        {group.cards.map((card) => {
          const author = teamUsers.find((u) => u.id === card.authorId);

          return (
            <div key={card.id} className={"flex gap-2 mb-4"}>
              <UserAvatar
                size={"sm"}
                avatarUrl={author?.avatar_link}
                name={author?.nick}
              />

              {card.text}
            </div>
          );
        })}

        <span className={"flex justify-end items-end mt-auto text-sm"}>
          {group.votes.length}{" "}
          {pluralText(group.votes.length, {
            one: "głos",
            few: "głosy",
            other: "głosów",
          })}
        </span>
      </div>

      <div className={"flex gap-4 justify-end"}>
        {hasCurrentUserVotedOnDiscussedGroup && (
          <Badge>zagłosowałeś/aś na ten temat</Badge>
        )}
      </div>
    </div>
  );
};

const ActionPointsSection: React.FC = () => {
  const {
    teamUsers,
    activeUsers,
    createTask,
    deleteTask,
    updateTask,
    setCreatingTask,
    tasks,
    discussionCardId,
  } = useRetro();
  const { user } = useUser();
  const [value, setValue] = useState("");

  const usersWritingTasks = useMemo(() => {
    const socketUsers = activeUsers.filter((user) => user.isCreatingTask);

    return teamUsers.filter((teamUser) => {
      return socketUsers.some(
        (socketUser) => socketUser.userId === teamUser.id,
      );
    });
  }, [activeUsers, teamUsers]);

  useEffect(() => {
    const onStopWriting = () => {
      setCreatingTask(false);
    };

    const isCurrentlyCreatingTask = activeUsers.find(
      (u) => u.userId === user?.id,
    )?.isCreatingTask;

    if (value !== "" && !isCurrentlyCreatingTask) {
      setCreatingTask(true);
    }
    const timeout = setTimeout(onStopWriting, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [value]);

  return (
    <Sidebar side={"right"} variant="floating" collapsible={"none"}>
      <SidebarHeader>
        <span className={"text-xl"}>Action pointy</span>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup className={"flex flex-col gap-2"}>
          {tasks
            ?.filter(
              (actionPoint) => actionPoint.parentCardId === discussionCardId,
            )
            .map((actionPoint) => {
              const author = teamUsers.find(
                (teamUser) => teamUser.id === actionPoint.ownerId,
              );

              return (
                <Card id={actionPoint.id} key={actionPoint.id}>
                  <CardContent
                    text={actionPoint.description}
                    editable
                    onSave={(text) => {
                      updateTask(actionPoint.id, author?.id ?? null, text);
                    }}
                  />
                  <CardAuthor
                    author={
                      author
                        ? {
                            avatar: author.avatar_link,
                            name: author.nick,
                            id: author.id,
                          }
                        : undefined
                    }
                    teamUsers={teamUsers.map((user) => ({
                      id: user.id,
                      name: user.nick,
                      avatar: user.avatar_link,
                    }))}
                    editable
                    onUserChange={(ownerId) => {
                      updateTask(
                        actionPoint.id,
                        ownerId,
                        actionPoint.description,
                      );
                    }}
                  />
                  <CardActions>
                    <Button
                      size={"icon"}
                      variant={"destructive"}
                      onClick={() => deleteTask(actionPoint.id)}
                    >
                      <TrashIcon className={"size-4"} />
                    </Button>
                  </CardActions>
                </Card>
              );
            })}
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className={"relative pb-25"}>
        <div className={"absolute top-2 flex gap-1 w-full px-1 h-0 -mt-6"}>
          {usersWritingTasks.slice(0, 8).map((user) => (
            <UserAvatar
              key={user.id}
              size={"sm"}
              className={"animate-bounce"}
              avatarUrl={user.avatar_link}
              name={user.nick}
            />
          ))}
        </div>

        <Textarea
          value={value}
          className={"resize-none min-h-20"}
          onChange={(event) => setValue(event.target.value)}
          placeholder={"Nowy action point..."}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !!user) {
              e.preventDefault();
              createTask(value, user.id);
              setValue("");
            }
          }}
        />
      </SidebarFooter>
    </Sidebar>
  );
};
